import React, { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import layoutVariations from './layoutVariations';
import { BlockType } from '../../../types/api/ElementType';
import { Namespace } from '../../../translation/namespace';
import { BlockInteractions } from '../../organisms/BlockInteractions';
import { RichTextBlock } from '../../../_blocks/RichText';
import {
  ContentWrapperContext,
  ContentWrapperContextProvider,
  useContentWrapperContext,
} from '../ContentWrapper/ContentWrapperContext';
import { ContentImageBlock } from '../../../_blocks/ContentImage/ContentImageBlock';
import { useContentWrapperDndContext } from '../ContentWrapper/ContentWrapperDndProvider';
import { BlockWithAction } from '../ContentWrapper/ContentWrapper.types';
import { ExternalContentBlock } from '../../../_blocks/ExternalContent';

const LayoutGroupContext = createContext<keyof typeof layoutVariations | undefined>(undefined);
export const useLayoutGroupContext = () => useContext(LayoutGroupContext);

export type LayoutGroup = {
  blocks: (
    | BlockWithAction<RichTextBlock>
    | BlockWithAction<ContentImageBlock>
    | BlockWithAction<ExternalContentBlock>
  )[];
  groupLayout: keyof typeof layoutVariations;
  uuid: string;
  elementType: BlockType.layoutgroup;
};

/**
 * This is not really a Block, but rather a 'glue' component used in Contentwrapper.
 * it has elementType, but no embedded so it differs vastly from other blocks.
 * we can not use asBlock() here !
 */
export function LayoutGroup({ blocks, groupLayout, uuid }: LayoutGroup) {
  const contentWrapperProps = useContentWrapperContext();
  const contentWrapperDndProps = useContentWrapperDndContext();
  const { t } = useTranslation(Namespace.LayoutGroup);
  // we cannot use editModeInfo from GlobalAppContext here:
  // In FaqList we display ContentWrappers from childPages that should not be in
  // edit mode but in default mode. To distinguish between these to cases we check if
  // all the grouped blocks have an editView.
  const editModeActive = blocks?.every(
    block => block.fetchType === 'instant' && block.embedded?.views?.edit !== undefined,
  );

  const className = `layout-group${
    editModeActive
      ? ' layout-group--edit-mode'
      : ` layout-group--default-mode layout-group--${layoutVariations[groupLayout]}`
  }`;

  const getLayoutGroupBlocks = (elementType: BlockType, block: any) => {
    switch (elementType) {
      case BlockType.contentimage:
        return <ContentImageBlock key={block.uuid} {...block} />;
      case BlockType.externalcontent:
        return <ExternalContentBlock key={block.uuid} {...block} />;
      case BlockType.richtext:
        return <RichTextBlock key={block.uuid} {...block} />;
      default:
        return null;
    }
  };

  if (!editModeActive) {
    return (
      <div className={className}>
        <div className="layout-group__inner">
          <LayoutGroupContext.Provider value={groupLayout}>
            {blocks.map(block => getLayoutGroupBlocks(block.elementType, block))}
          </LayoutGroupContext.Provider>
        </div>
      </div>
    );
  }

  return (
    <BlockInteractions
      contentWrapperProps={contentWrapperProps}
      contentWrapperDndProps={contentWrapperDndProps}
      views={{ default: {}, edit: {} }}
      uuid={uuid}
      elementType={BlockType.layoutgroup}
      headline={{
        hierarchy: 2,
        visible: true,
        text: editModeActive ? t('headline') : null,
      }}
    >
      <div className={className}>
        <div className="layout-group__inner">
          {blocks.map((block, index) => (
            <ContentWrapperContextProvider
              key={block.uuid}
              {...(contentWrapperProps as ContentWrapperContext)}
              block={block}
              baseIndex={contentWrapperProps?.index} // baseIndex is the LayoutGroups' index inside the
              index={index}
            >
              <LayoutGroupContext.Provider value={groupLayout}>
                {getLayoutGroupBlocks(block.elementType, block)}
              </LayoutGroupContext.Provider>
            </ContentWrapperContextProvider>
          ))}
        </div>
      </div>
    </BlockInteractions>
  );
}
